import { useRef } from 'react';

import { RostosDoReconhecimento } from 'components/person-avatar/rostos-do-reconhecimento';
import { exposeFullMessageHeight, MENSAGEM_RECORTADA } from 'components/mensagem-recortada';
import { RecentRecognition } from 'pages/Dashboard/components/recent-recognitions';

import { corDaMoeda } from '../feed-calculos';
import { ROTA_DOS_RECONHECIMENTOS_DO_PREFIXO } from '../rotas-do-feed';
import CabecalhoDeSecao from './CabecalhoDeSecao';
import { FOCO_VISIVEL } from 'utils/foco';

const MAXIMO = 3;

// Inclinação, sombra e cor do avatar de cada cartão, como no design.
export const ESTILO_DO_CARTAO = [
  { giro: '-rotate-[1.6deg]', sombra: 'shadow-[0_12px_26px_rgba(0,45,75,0.24)]', avatar: 'bg-brand-pink-light' },
  { giro: 'rotate-[1.1deg]', sombra: 'shadow-[0_12px_26px_rgba(0,45,75,0.22)]', avatar: 'bg-brand-cyan-light' },
  { giro: '-rotate-[0.8deg]', sombra: 'shadow-[0_12px_26px_rgba(0,45,75,0.22)]', avatar: 'bg-brand-purple-light' },
];

/**
 * Um reconhecimento do prefixo. Os rostos de quem enviou e de quem recebeu (AJ3-05), a mensagem cortada em 3 linhas
 * e inteira com o mouse ou o foco no cartão, e só o comportamento no rodapé, sem os pontos (AJH-54 a AJH-56).
 */
export function CartaoDeReconhecimento({ item, estilo }: { item: RecentRecognition; estilo: (typeof ESTILO_DO_CARTAO)[number] }) {
  const mensagemRef = useRef<HTMLParagraphElement>(null);
  const expandir = () => exposeFullMessageHeight(mensagemRef.current);

  return (
    <li
      tabIndex={0}
      onMouseEnter={expandir}
      onFocus={expandir}
      className={`group flex flex-col gap-2.5 rounded-[18px] bg-surface-0 px-[18px] py-4 text-content transition duration-[180ms] hover:-translate-y-[5px] hover:shadow-[0_18px_34px_rgba(0,45,75,0.3)] motion-reduce:hover:translate-y-0 ${estilo.giro} ${estilo.sombra} ${FOCO_VISIVEL}`}
    >
      <div className="flex items-center gap-[11px]">
        <RostosDoReconhecimento
          item={item}
          tamanho={40}
          iniciaisDoAutor={`font-display text-[13px] font-extrabold ${estilo.avatar}`}
          iniciaisDoDestinatario="bg-surface-200 font-display text-[13px] font-extrabold text-content"
        />
        <div className="min-w-0">
          <h3 className="m-0 font-display text-[14px] font-bold leading-[1.1]">{item.author} reconheceu</h3>
          <p className="m-0 text-[12px] text-content-soft">
            {item.recipient} · {item.when}
          </p>
        </div>
        {/* Chip da moeda: o rótulo vem da Home (HOME-19), pela moeda gravada ou pela faixa de pontos. */}
        {item.level && (
          <span className={`ml-auto rounded-jj-pill px-[11px] py-[5px] font-display text-[10px] font-bold tracking-[0.04em] ${corDaMoeda(item.level)}`}>
            {item.level}
          </span>
        )}
      </div>
      <p ref={mensagemRef} className={`m-0 text-[13px] ${MENSAGEM_RECORTADA}`}>
        “{item.message}”
      </p>
      <div className="mt-auto border-t border-solid border-[color:var(--border-default)] pt-[11px]">
        <span className="font-display text-[11px] font-bold uppercase tracking-[0.05em] text-content opacity-[0.68]">
          {item.behavior}
        </span>
      </div>
    </li>
  );
}

interface UltimosReconhecimentosProps {
  prefixo?: number;
  itens: RecentRecognition[];
}

/** "Últimos reconhecimentos" do prefixo, do cache que a Home já lê (REDE-04). */
export default function UltimosReconhecimentos({ prefixo, itens }: UltimosReconhecimentosProps) {
  if (!itens.length) return null;

  return (
    <section aria-labelledby="titulo-ultimos-reconhecimentos" className="mt-7 flex flex-col gap-3.5">
      <CabecalhoDeSecao
        id="titulo-ultimos-reconhecimentos"
        titulo={prefixo ? `Últimos reconhecimentos - seu prefixo ${prefixo}` : 'Últimos reconhecimentos'}
        link={{ para: ROTA_DOS_RECONHECIMENTOS_DO_PREFIXO, rotulo: 'Ver todos do prefixo' }}
      />

      <ul className="m-0 grid list-none grid-cols-1 gap-5 px-1 pb-2.5 pt-2 md:grid-cols-3">
        {itens.slice(0, MAXIMO).map((item, indice) => {
          return <CartaoDeReconhecimento key={item.id} item={item} estilo={ESTILO_DO_CARTAO[indice % ESTILO_DO_CARTAO.length]} />;
        })}
      </ul>
    </section>
  );
}
